/**
 * Settings → Users.
 *
 * Answers three questions at a glance, which is what the screen is for: who has signed
 * in and how, what role they hold and whether a person or a rule decided it, and — for
 * an Account Manager — which Accounts they look after. That last column is the one that
 * matters most: an [ACCT] sitting at 0 Accounts can see and plan for nothing, which was
 * the reported "Account Managers can't create plans" bug.
 */

import { Eye, MoreHorizontal, Plus, Users } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { USER_ROLES, type UserRow, useUsers } from "@/hooks/use-users";
import { useStartViewAs } from "@/hooks/use-view-as";
import { useSession } from "@/lib/auth-client";
import { canViewAs } from "@/lib/view-as";
import { AccessDialog } from "./AccessDialog";
import { ChangeRoleDialog } from "./ChangeRoleDialog";
import { CreateUserDialog } from "./CreateUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { ManageAccountOwnershipDialog } from "./ManageAccountOwnershipDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import { RoleRuleCard } from "./RoleRuleCard";
import { UserDetailDialog } from "./UserDetailDialog";
import { formatSignIn, ProviderBadge, RoleSourceBadge } from "./user-presentation";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "detail"; userId: string }
  | { type: "resetPassword"; user: UserRow }
  | { type: "changeRole"; user: UserRow }
  | { type: "accounts"; user: UserRow }
  | { type: "access"; user: UserRow }
  | { type: "delete"; user: UserRow };

const PAGE_SIZE = 25;

export function UsersTable() {
  const { data: session } = useSession();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [role, setRole] = useState("all");
  const [provider, setProvider] = useState("all");
  const [status, setStatus] = useState("all");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const startViewAs = useStartViewAs();

  const { data, isLoading, isError, error, refetch } = useUsers({
    search: debouncedSearch || undefined,
    role,
    provider,
    status,
    cursor,
    direction,
    limit: PAGE_SIZE,
  });

  /** Any filter change invalidates the cursor — otherwise page 3 of the old filter leaks. */
  function applyFilter(set: (value: string) => void, value: string) {
    set(value);
    setCursor(undefined);
    setDirection("next");
    setPage(1);
  }

  function closeDialog() {
    setDialog({ type: "none" });
  }

  const users = data?.data ?? [];
  const selfId = session?.user?.id;
  // View As is ADMIN-only (the server refuses everyone else), and pointless on yourself.
  const showViewAs = canViewAs(session?.user?.role);

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load users"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <header>
          <h2 className="font-display text-lg font-semibold">Users</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Everyone who can sign in, the role they hold, and — for Account Managers — the Accounts
            they look after.
          </p>
        </header>

        <div className="flex flex-wrap items-center gap-2">
          <SearchInput
            value={search}
            onValueChange={(value) => {
              setSearch(value);
              setCursor(undefined);
              setPage(1);
            }}
            placeholder="Search by name or email..."
            className="min-w-56 flex-1"
          />

          <Select value={role} onValueChange={(value) => applyFilter(setRole, value)}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {USER_ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={provider} onValueChange={(value) => applyFilter(setProvider, value)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Sign-in" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any sign-in</SelectItem>
              <SelectItem value="microsoft">Microsoft</SelectItem>
              <SelectItem value="google">Google</SelectItem>
              <SelectItem value="credential">Password</SelectItem>
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={(value) => applyFilter(setStatus, value)}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Access" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any access</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>

          <Button onClick={() => setDialog({ type: "create" })}>
            <Plus className="mr-2 h-4 w-4" />
            Add user
          </Button>
        </div>

        <DataTable
          headers={[
            { label: "Name" },
            { label: "Sign-in" },
            { label: "Role" },
            { label: "Access" },
            { label: "Last sign-in", className: "hidden lg:table-cell" },
            { label: "Accounts", className: "text-right" },
            { label: "", className: "text-right" },
          ]}
          loading={isLoading}
          empty={
            <EmptyState
              icon={Users}
              title="No users found"
              description="Try a different search or filter."
            />
          }
        >
          {users.map((user) => (
            <tr
              key={user.id}
              className="cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/30"
              onClick={() => setDialog({ type: "detail", userId: user.id })}
            >
              <td className="px-4 py-3">
                <div className="font-medium">{user.name}</div>
                <div className="text-xs text-muted-foreground">{user.email}</div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {user.providers.length === 0 ? (
                    <Badge variant="outline">Password</Badge>
                  ) : (
                    user.providers.map((p) => <ProviderBadge key={p} provider={p} />)
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-1">
                  <Badge variant="secondary">{user.role}</Badge>
                  <RoleSourceBadge source={user.roleSource} />
                </div>
              </td>
              <td className="px-4 py-3">
                <Badge variant={user.accessStatus === "active" ? "secondary" : "destructive"}>
                  {user.accessStatus === "active" ? "Active" : "Disabled"}
                </Badge>
              </td>
              <td className="hidden px-4 py-3 text-muted-foreground lg:table-cell">
                {formatSignIn(user.lastSignInAt)}
              </td>
              <td className="px-4 py-3 text-right">
                {user.role === "ACCT" && user.ownedAccountCount === 0 ? (
                  // An Account Manager with no Accounts can reach nothing. Say so here
                  // rather than leaving a bare 0 that reads like a fine number.
                  <Badge variant="destructive" title="This Account Manager cannot reach anything">
                    None assigned
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">{user.ownedAccountCount}</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Actions for ${user.name}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                    {showViewAs && (
                      <>
                        <DropdownMenuItem
                          // Better Auth refuses any session for a disabled user, View As included.
                          disabled={
                            user.id === selfId ||
                            user.accessStatus !== "active" ||
                            startViewAs.isPending
                          }
                          onClick={() => startViewAs.mutate(user.id)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View as {user.name.split(" ")[0] || "user"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => setDialog({ type: "accounts", user })}>
                      Accounts
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={user.id === selfId}
                      onClick={() => setDialog({ type: "changeRole", user })}
                    >
                      Change role
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={user.id === selfId}
                      onClick={() => setDialog({ type: "access", user })}
                    >
                      {user.accessStatus === "active" ? "Disable access" : "Restore access"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDialog({ type: "resetPassword", user })}>
                      Reset password
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      disabled={user.id === selfId}
                      onClick={() => setDialog({ type: "delete", user })}
                    >
                      Delete user
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </DataTable>

        <CursorPagination
          total={data?.total ?? 0}
          page={page}
          limit={PAGE_SIZE}
          hasNext={data?.hasNext ?? false}
          hasPrev={data?.hasPrev ?? false}
          loading={isLoading}
          itemLabel="users"
          onNext={() => {
            setCursor(data?.nextCursor ?? undefined);
            setDirection("next");
            setPage((p) => p + 1);
          }}
          onPrev={() => {
            setCursor(data?.prevCursor ?? undefined);
            setDirection("prev");
            setPage((p) => Math.max(1, p - 1));
          }}
        />
      </div>

      <RoleRuleCard />

      <CreateUserDialog
        open={dialog.type === "create"}
        onOpenChange={(open) => !open && closeDialog()}
      />

      <UserDetailDialog
        open={dialog.type === "detail"}
        onOpenChange={(open) => !open && closeDialog()}
        userId={dialog.type === "detail" ? dialog.userId : null}
        isSelf={dialog.type === "detail" && dialog.userId === selfId}
        onChangeRole={(user) => setDialog({ type: "changeRole", user })}
        onManageAccounts={(user) => setDialog({ type: "accounts", user })}
        onSetAccess={(user) => setDialog({ type: "access", user })}
        onResetPassword={(user) => setDialog({ type: "resetPassword", user })}
        onDelete={(user) => setDialog({ type: "delete", user })}
      />

      {dialog.type === "resetPassword" && (
        <ResetPasswordDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "changeRole" && (
        <ChangeRoleDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "accounts" && (
        <ManageAccountOwnershipDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "access" && (
        <AccessDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "delete" && (
        <DeleteUserDialog open onOpenChange={(open) => !open && closeDialog()} user={dialog.user} />
      )}
    </div>
  );
}
